import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  api,
  formatLastSeen,
  type DeviceDetail,
  type DeviceLogEntry,
} from '../api/client'
import type { NetworkSnapshot, Finding } from '../api/types'
import {
  StatusIndicator,
  LocationBadge,
  Sparkline,
  SeverityBadge,
} from '../components/StatusIndicator'
import { FindingsPanel } from '../components/FindingsPanel'
import { sortWifiScansBySignal } from './wifiScans'

// ---------------------------------------------------------------------------
// Compact info pair for dense data (label left, value right, no border)
// ---------------------------------------------------------------------------
function InfoPair({ label, value, mono = false }: {
  label: string
  value: string | number | null | undefined
  mono?: boolean
}) {
  if (value == null || value === '') return null
  return (
    <div className="flex justify-between items-baseline gap-2 py-1">
      <dt className="text-slate-500 text-xs shrink-0">{label}</dt>
      <dd className={`text-slate-200 text-xs text-right truncate ${mono ? 'font-mono' : ''}`}>{value}</dd>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section card container
// ---------------------------------------------------------------------------
function SectionCard({ title, children, className = '' }: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`bg-slate-800/30 border border-slate-700/40 rounded-lg ${className}`}>
      <div className="px-4 py-2.5 border-b border-slate-700/30">
        <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">{title}</h3>
      </div>
      <div className="px-4 py-3">{children}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metric tile for network quality
// ---------------------------------------------------------------------------
function MetricTile({ value, unit, label, warn = false, compact = false }: {
  value: string | number
  unit?: string
  label: string
  warn?: boolean
  compact?: boolean
}) {
  return (
    <div className={`text-center ${compact ? 'py-1.5' : 'py-3'}`}>
      <div className={`font-mono leading-none ${warn ? 'text-amber-400' : 'text-white'} ${compact ? 'text-lg' : 'text-2xl'}`}>
        {value}<span className="text-xs text-slate-500 ml-0.5">{unit}</span>
      </div>
      <div className="text-[10px] text-slate-500 mt-1 uppercase tracking-wide">{label}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Gauge bar for system metrics (compact inline)
// ---------------------------------------------------------------------------
function GaugeRowDetailed({ label, value, detail, warn = false, invert = false, forceOk = false }: {
  label: string; value: number; detail?: string; warn?: boolean; invert?: boolean; forceOk?: boolean
}) {
  const pct = Math.min(100, Math.max(0, value))
  // forceOk: always green (battery on AC), invert: low is bad (battery on DC), default: high is bad (CPU/mem/disk)
  const color = warn ? 'bg-red-400'
    : forceOk ? 'bg-emerald-400'
    : invert
      ? (pct < 5 ? 'bg-red-400' : pct < 15 ? 'bg-amber-400' : 'bg-emerald-400')
      : (pct > 90 ? 'bg-red-400' : pct > 70 ? 'bg-amber-400' : 'bg-emerald-400')
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="text-slate-500 text-xs w-8 shrink-0">{label}</span>
        <div className="flex-1 bg-slate-700/50 rounded-full h-1.5 overflow-hidden">
          <div className={`${color} h-full rounded-full`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-slate-300 text-xs font-mono w-8 text-right">{Math.round(pct)}%</span>
      </div>
      {detail && (
        <div className="text-[10px] text-slate-600 font-mono ml-10">{detail}</div>
      )}
    </div>
  )
}

function formatTB(gb: number): string {
  if (gb >= 1000) return `${(gb / 1000).toFixed(1)} TB`
  return `${Math.round(gb)} GB`
}

// ---------------------------------------------------------------------------
// Connection type badge
// ---------------------------------------------------------------------------
function ConnectionTypeBadge({ type, interfaceName, adapterName }: {
  type: 'ethernet' | 'wifi' | 'tethered' | 'vpn'
  interfaceName?: string
  adapterName?: string | null
}) {
  const config = {
    ethernet: { label: 'Ethernet', icon: '🔌', bg: 'bg-blue-500/10 border-blue-500/20', text: 'text-blue-400' },
    wifi: { label: 'WiFi', icon: '📶', bg: 'bg-violet-500/10 border-violet-500/20', text: 'text-violet-400' },
    tethered: { label: 'Tethered', icon: '📱', bg: 'bg-amber-500/10 border-amber-500/20', text: 'text-amber-400' },
    vpn: { label: 'VPN', icon: '🔒', bg: 'bg-cyan-500/10 border-cyan-500/20', text: 'text-cyan-400' },
  }[type]

  const detail = adapterName || interfaceName
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${config.bg} ${config.text}`}>
      <span>{config.icon}</span>
      <span>{config.label}</span>
      {detail && <span className="text-slate-500 font-normal font-mono">({detail})</span>}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Extract network quality from full NetworkSnapshot performance[] array
// and observation.classifiedTrace.hops[] for gateway metrics
// ---------------------------------------------------------------------------
function extractNetworkQuality(health: NetworkSnapshot) {
  // Sort windows by duration (prefer shortest with data)
  const windows = [...(health.performance ?? [])].sort((a, b) => a.windowSeconds - b.windowSeconds)
  const shortWindow = windows[0]
  if (!shortWindow) return null

  // Use shortest window, but fall back to longer windows for metrics
  // that need multiple samples (e.g. jitter requires 2+ samples)
  const destLatency = shortWindow.averageLatencyMs ?? null
  const destLoss = shortWindow.averagePacketLossPercent ?? null
  const destJitter = shortWindow.averageJitterMs
    ?? windows.find(w => w.averageJitterMs != null)?.averageJitterMs
    ?? null

  // Gateway metrics from classified trace LOCAL hop
  const gwHop = health.observation?.classifiedTrace?.hops?.find(h => h.category === 'LOCAL')
  const gwPing = gwHop?.pingStats30s

  return {
    gatewayLatencyMs: gwPing?.avgLatencyMs ?? null,
    gatewayLossPercent: gwPing?.lossPercent ?? null,
    gatewayJitterMs: gwPing?.jitterMs ?? null,
    destinationLatencyMs: destLatency,
    destinationLossPercent: destLoss,
    destinationJitterMs: destJitter,
  }
}

// ===========================================================================
// Main component
// ===========================================================================

export default function DeviceDetailPage() {
  const { orgId, clientId } = useParams<{ orgId: string; clientId: string }>()
  const navigate = useNavigate()
  const [device, setDevice] = useState<DeviceDetail | null>(null)
  const [health, setHealth] = useState<NetworkSnapshot | null>(null)
  const [findings, setFindings] = useState<Finding[]>([])
  const [findingsLoading, setFindingsLoading] = useState(true)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!clientId) return

    api.getDevice(clientId)
      .then(res => setDevice(res.data))
      .catch(err => console.error('Failed to load device:', err))
      .finally(() => setLoading(false))

    api.getDeviceHealth(clientId)
      .then(res => setHealth(res))
      .catch(() => {}) // 404 = no snapshot, fine

    api.getDeviceFindings(clientId)
      .then(res => setFindings(res.data))
      .catch(() => {})
      .finally(() => setFindingsLoading(false))
  }, [clientId])

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading device...</div>
  }

  if (!device) {
    return <div className="p-8 text-center text-slate-500">Device not found</div>
  }

  const hasLocation = device.location && device.location.type !== 'unknown'
  const nq = health ? extractNetworkQuality(health) : null
  const dnq = device.networkQuality

  // Aliases for NetworkSnapshot sub-objects
  const gateway = health?.status?.gateway
  const sysHealth = health?.systemHealth
  const dns = health?.systemDNS
  const issues = health?.observation?.issues ?? []
  const traceHops = health?.observation?.classifiedTrace?.hops ?? []
  const classifiedTrace = health?.observation?.classifiedTrace
  // First ISP hop metrics
  const firstIspHop = traceHops.find(h => h.category === 'ISP')
  const wifiScans = sortWifiScansBySignal(health?.wifiScans ?? [])
  const primaryIfName = health?.path?.primaryInterface
  const activeInterface = primaryIfName
    ? health?.interfaces?.find(i => i.name === primaryIfName)
    : health?.interfaces?.find(i => !i.vpnType)

  // Machine name: prefer computerName, fall back to localHostname
  const machineName = health?.metadata?.computerName
    ?? health?.metadata?.localHostname?.replace(/\.(local|localdomain)$/, '')
    ?? null

  // WiFi MAC from en0 interface (now included in snapshot)
  const en0Iface = health?.interfaces?.find(i => i.name === 'en0')
  const en0MAC = en0Iface?.macAddress ?? null

  // Determine connection type from primary interface + snapshot data
  const connectionType: 'ethernet' | 'wifi' | 'tethered' | 'vpn' | null = (() => {
    if (!health) return null
    if (health.tether) return 'tethered'
    if (health.vpn?.isConnected && activeInterface?.vpnType) return 'vpn'
    if (activeInterface?.hardwareInfo?.productName || activeInterface?.hardwareInfo?.actualLinkSpeed) return 'ethernet'
    if (health.wifi?.ssid && primaryIfName?.startsWith('en')) return 'wifi'
    if (health.wifi?.ssid) return 'wifi'
    return null
  })()

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/orgs/${orgId}`)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4 group"
        >
          <svg className="w-4 h-4 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Devices
        </button>

        <div className="flex items-center gap-3">
          <StatusIndicator status={device.status} size="lg" pulse={device.status === 'critical'} />
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              {device.assetId || device.userName || 'Device Details'}
            </h1>
            <p className="text-slate-500 text-sm font-mono">{device.clientId}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4 max-w-4xl">
        {/* Status banner */}
        <div className={`
          rounded-lg px-4 py-3
          ${device.status === 'healthy' ? 'bg-emerald-500/10 border border-emerald-500/20' : ''}
          ${device.status === 'warning' ? 'bg-amber-500/10 border border-amber-500/20' : ''}
          ${device.status === 'critical' ? 'bg-red-500/10 border border-red-500/20' : ''}
        `}>
          <div className="flex items-center gap-2">
            <StatusIndicator status={device.status} />
            <span className={`font-medium ${
              device.status === 'healthy' ? 'text-emerald-400' :
              device.status === 'warning' ? 'text-amber-400' : 'text-red-400'
            }`}>
              {device.status === 'healthy' ? 'Device Healthy' :
               device.status === 'warning' ? 'Needs Attention' : 'Device Offline'}
            </span>
          </div>
          <p className="text-slate-400 text-sm mt-1">
            {device.status === 'healthy' ? 'Online and running the latest version.' :
             device.status === 'warning' ? 'Device may need updates or hasn\'t checked in recently.' :
             'Device has not checked in for over 24 hours.'}
          </p>
          {connectionType && (
            <div className="flex items-center gap-2 mt-2">
              <ConnectionTypeBadge
                type={connectionType}
                interfaceName={activeInterface?.name}
                adapterName={activeInterface?.friendlyName || activeInterface?.hardwareInfo?.productName}
              />
            </div>
          )}
        </div>

        {/* ================================================================
            NETWORK QUALITY
            ================================================================ */}
        {(dnq || nq) ? (
          <div className="bg-slate-800/30 border border-slate-700/40 rounded-lg overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700/30 flex items-center justify-between">
              <h3 className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest">Network Quality</h3>
              {health?.capturedAt && (
                <span className="text-[10px] text-slate-600 font-mono">
                  snapshot {formatLastSeen(health.capturedAt)}
                </span>
              )}
            </div>
            {nq ? (
              <div className="grid grid-cols-2 divide-x divide-slate-700/30">
                {/* Gateway */}
                <div className="p-3">
                  <div className="text-[10px] text-slate-600 uppercase tracking-wide text-center mb-2">Gateway</div>
                  <div className="grid grid-cols-3 gap-2">
                    <MetricTile value={nq.gatewayLatencyMs != null ? Math.round(nq.gatewayLatencyMs) : '--'} unit="ms" label="Latency" compact />
                    <MetricTile value={nq.gatewayLossPercent != null ? nq.gatewayLossPercent.toFixed(1) : '--'} unit="%" label="Loss" compact />
                    <MetricTile
                      value={nq.gatewayJitterMs != null ? Math.round(nq.gatewayJitterMs) : '--'}
                      unit="ms" label="Jitter" compact
                      warn={nq.gatewayJitterMs != null && nq.gatewayJitterMs > 5}
                    />
                  </div>
                </div>
                {/* Destination */}
                <div className="p-3">
                  <div className="text-[10px] text-slate-600 uppercase tracking-wide text-center mb-2">
                    Internet{health?.observation?.destination ? ` \u2192 ${health.observation.destination}` : ''}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <MetricTile
                      value={nq.destinationLatencyMs != null ? Math.round(nq.destinationLatencyMs) : '--'}
                      unit="ms" label="Latency" compact
                      warn={nq.destinationLatencyMs != null && nq.destinationLatencyMs > 50}
                    />
                    <MetricTile
                      value={nq.destinationLossPercent != null ? nq.destinationLossPercent.toFixed(1) : '--'}
                      unit="%" label="Loss" compact
                      warn={nq.destinationLossPercent != null && nq.destinationLossPercent > 1}
                    />
                    <MetricTile
                      value={nq.destinationJitterMs != null ? Math.round(nq.destinationJitterMs) : '--'}
                      unit="ms" label="Jitter" compact
                      warn={nq.destinationJitterMs != null && nq.destinationJitterMs > 10}
                    />
                  </div>
                </div>
              </div>
            ) : (
              /* Fallback: device-level summary only */
              <div className="p-3">
                <div className="grid grid-cols-3 gap-3">
                  <MetricTile value={dnq?.latencyMs ?? '--'} unit="ms" label="Latency" compact />
                  <MetricTile value={dnq?.lossPercent != null ? `${dnq.lossPercent}` : '--'} unit="%" label="Loss" compact />
                  <MetricTile value={dnq?.jitterMs ?? '--'} unit="ms" label="Jitter" compact />
                </div>
              </div>
            )}

            {/* Sparklines */}
            {device.networkHistory && device.networkHistory.length > 0 && (
              <div className="border-t border-slate-700/30 grid grid-cols-2 divide-x divide-slate-700/30">
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-slate-600">Latency 24h</span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      avg {Math.round(device.networkHistory.reduce((a, b) => a + b.latencyMs, 0) / device.networkHistory.length)}ms
                    </span>
                  </div>
                  <Sparkline data={device.networkHistory.map(m => m.latencyMs)} color="#60a5fa" height={32} />
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-slate-600">Loss 24h</span>
                    <span className="text-[10px] text-slate-500 font-mono">
                      avg {(device.networkHistory.reduce((a, b) => a + b.lossPercent, 0) / device.networkHistory.length).toFixed(1)}%
                    </span>
                  </div>
                  <Sparkline data={device.networkHistory.map(m => m.lossPercent)} color="#f59e0b" height={32} />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-800/30 rounded-lg p-4 text-center border border-slate-700/40">
            <div className="text-slate-500 text-sm">No network data available</div>
          </div>
        )}

        {/* ================================================================
            TRACEROUTE
            ================================================================ */}
        {traceHops.length > 0 && (
          <SectionCard title="Network Path">
            <div className="flex items-center gap-2 mb-2">
              {health?.observation?.publicIP && (
                <span className="text-[10px] text-slate-600 font-mono">
                  Public IP: {health.observation.publicIP}
                </span>
              )}
              <span className="text-[10px] text-slate-600 ml-auto">
                {traceHops.length} hops to {health?.observation?.destination ?? 'destination'}
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-mono">
                <thead>
                  <tr className="text-[10px] text-slate-600 uppercase">
                    <th className="text-left py-1 pr-2 w-6">#</th>
                    <th className="text-left py-1 pr-2">IP</th>
                    <th className="text-left py-1 pr-2">Hostname</th>
                    <th className="text-right py-1 pr-2 w-14">RTT</th>
                    <th className="text-right py-1 pr-2 w-12">Loss</th>
                    <th className="text-right py-1 pr-2 w-14">Jitter</th>
                    <th className="text-right py-1 w-16">Segment</th>
                  </tr>
                </thead>
                <tbody>
                  {traceHops.map(hop => {
                    const ps = hop.pingStats30s
                    const catColor = {
                      LOCAL: 'text-emerald-400',
                      ISP: 'text-blue-400',
                      DESTINATION: 'text-violet-400',
                    }[hop.category] ?? 'text-slate-500'
                    return (
                      <tr key={hop.ttl} className="border-t border-slate-800/30 hover:bg-slate-800/20">
                        <td className="py-1 pr-2 text-slate-600">{hop.ttl}</td>
                        <td className="py-1 pr-2 text-slate-300">{hop.ip ?? '*'}</td>
                        <td className="py-1 pr-2 text-slate-500 truncate max-w-[200px]">{hop.hostname ?? ''}</td>
                        <td className="py-1 pr-2 text-right text-slate-300">
                          {ps?.avgLatencyMs != null ? `${ps.avgLatencyMs}ms` : '-'}
                        </td>
                        <td className="py-1 pr-2 text-right text-slate-300">
                          {ps ? `${ps.lossPercent}%` : '-'}
                        </td>
                        <td className="py-1 pr-2 text-right text-slate-300">
                          {ps?.jitterMs != null ? `${ps.jitterMs}ms` : '-'}
                        </td>
                        <td className={`py-1 text-right text-[10px] ${catColor}`}>
                          {hop.category}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

        {/* ================================================================
            CULPRIT + ISSUES (actionable, stays prominent)
            ================================================================ */}
        {health?.culprit && (
          <div className="bg-slate-800/30 border border-slate-700/40 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <SeverityBadge severity={health.culprit.severity} />
              <span className="text-white text-sm font-medium">{health.culprit.title}</span>
              <span className="text-[10px] text-slate-600 font-mono ml-auto">{health.culprit.segment}</span>
            </div>
            <p className="text-slate-400 text-sm">{health.culprit.description}</p>
            {health.culprit.remedy && (
              <p className="text-slate-300 text-xs mt-2 pt-2 border-t border-slate-700/30">
                <span className="text-slate-500 uppercase text-[10px] mr-1">Remedy:</span>
                {health.culprit.remedy}
              </p>
            )}
          </div>
        )}

        {issues.length > 0 && (
          <div className="space-y-1.5">
            {issues.map((issue, i) => (
              <div key={i} className="flex items-center gap-3 bg-slate-800/30 border border-slate-700/40 rounded-lg px-4 py-2.5">
                <SeverityBadge severity={issue.severity} />
                <span className="text-slate-300 text-sm flex-1">{issue.description}</span>
                <span className="text-[10px] text-slate-600 font-mono">{issue.category}</span>
              </div>
            ))}
          </div>
        )}

        {/* ================================================================
            SCAN FINDINGS (from client findings sync)
            ================================================================ */}
        {(findings.length > 0 || findingsLoading) && (
          <SectionCard title="Scan Findings">
            <FindingsPanel findings={findings} loading={findingsLoading} />
          </SectionCard>
        )}

        {/* ================================================================
            TWO-COLUMN GRID: WiFi + Gateway | System + VPN/DNS
            ================================================================ */}
        {health && (health.wifi || gateway || sysHealth || health.vpn || dns) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* LEFT COLUMN: Network infrastructure */}
            <div className="space-y-4">
              {health.wifi && (
                <SectionCard title="WiFi">
                  {health.wifiAdvisory && (
                    <div className={`rounded px-3 py-1.5 text-xs mb-3 ${
                      health.wifiAdvisory.quality === 'good' ? 'bg-emerald-500/10 text-emerald-400' :
                      health.wifiAdvisory.quality === 'fair' ? 'bg-amber-500/10 text-amber-400' :
                      'bg-red-500/10 text-red-400'
                    }`}>
                      {health.wifiAdvisory.message}
                    </div>
                  )}
                  <dl className="space-y-0.5">
                    <InfoPair label="SSID" value={health.wifi.ssid} mono />
                    <InfoPair label="BSSID" value={health.wifi.bssid} mono />
                    {health.wifi.rssi != null && (
                      <InfoPair label="Signal" value={`${health.wifi.rssi} dBm${health.wifi.noiseMeasurement != null ? ` / ${health.wifi.noiseMeasurement} dBm (SNR ${health.wifi.rssi - health.wifi.noiseMeasurement})` : ''}`} />
                    )}
                    {health.wifi.channel != null && (
                      <InfoPair label="Channel" value={`${health.wifi.channel}${health.wifi.channelWidth ? ` (${health.wifi.channelWidth} MHz)` : ''}`} />
                    )}
                    <InfoPair label="Protocol" value={health.wifi.wifiProtocol ? `802.11${health.wifi.wifiProtocol}` : undefined} />
                    <InfoPair label="TX Rate" value={health.wifi.transmitRate != null ? `${health.wifi.transmitRate} Mbps` : undefined} />
                    <InfoPair label="Security" value={health.wifi.security} />
                    {(health.wifi.accessPointName || health.wifi.accessPointModel) && (
                      <InfoPair label="Access Point" value={[health.wifi.accessPointName, health.wifi.accessPointModel].filter(Boolean).join(' / ')} />
                    )}
                  </dl>
                </SectionCard>
              )}

              {/* Radio environment */}
              {wifiScans.length > 0 && (
                <SectionCard title={`Radio Environment (${wifiScans.length} networks)`}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                      <thead>
                        <tr className="text-[10px] text-slate-600 uppercase">
                          <th className="text-left py-1 pr-2">SSID</th>
                          <th className="text-left py-1 pr-2">BSSID</th>
                          <th className="text-right py-1 pr-2 w-12">RSSI</th>
                          <th className="text-right py-1 pr-2 w-8">Ch</th>
                          <th className="text-left py-1 w-20">Security</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wifiScans.map((net, i) => {
                          const isConnected = health?.wifi?.bssid === net.bssid
                          return (
                            <tr key={i} className={`border-t border-slate-800/30 ${isConnected ? 'bg-emerald-500/5' : 'hover:bg-slate-800/20'}`}>
                              <td className={`py-1 pr-2 truncate max-w-[120px] ${isConnected ? 'text-emerald-400' : 'text-slate-300'}`}>
                                {net.ssid || <span className="text-slate-600 italic">hidden</span>}
                                {isConnected && <span className="text-[10px] text-emerald-600 ml-1">*</span>}
                              </td>
                              <td className="py-1 pr-2 text-slate-500">{net.bssid}</td>
                              {net.rssi == null ? (
                                <td className="py-1 pr-2 text-right text-slate-600" title="Signal unknown: the scan did not measure it">-</td>
                              ) : (
                                <td className={`py-1 pr-2 text-right ${
                                  net.rssi > -50 ? 'text-emerald-400' :
                                  net.rssi > -70 ? 'text-slate-300' :
                                  'text-amber-400'
                                }`}>{net.rssi}</td>
                              )}
                              <td className="py-1 pr-2 text-right text-slate-400">{net.channel}</td>
                              <td className="py-1 text-slate-500 text-[10px]">{net.security}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              )}

              {gateway && (
                <SectionCard title="Gateway">
                  <dl className="space-y-0.5">
                    {(gateway.vendor || gateway.model) && (
                      <InfoPair label="Device" value={[gateway.vendor, gateway.model].filter(Boolean).join(' ')} />
                    )}
                    <InfoPair label="IP" value={gateway.ip} mono />
                    <InfoPair label="Hostname" value={gateway.hostname} mono />
                    <InfoPair label="MAC" value={gateway.macAddress} mono />
                    {gateway.ouiVendor && !gateway.vendor && (
                      <InfoPair label="Vendor (OUI)" value={gateway.ouiVendor} />
                    )}
                    <InfoPair label="SSL Cert" value={gateway.gatewayCertCN} mono />
                    {gateway.uptimeSeconds != null && (
                      <InfoPair label="Uptime" value={formatUptime(gateway.uptimeSeconds)} />
                    )}
                    <InfoPair label="Detection" value={gateway.detectionMethod} />
                    {gateway.eolSeverity && (
                      <div className="flex justify-between items-center py-1">
                        <dt className="text-slate-500 text-xs">End of Life</dt>
                        <dd><SeverityBadge severity={gateway.eolSeverity} /></dd>
                      </div>
                    )}
                  </dl>
                </SectionCard>
              )}

              {/* DHCP & Network */}
              {(health.dhcp || activeInterface) && (
                <SectionCard title="Network Details">
                  <dl className="space-y-0.5">
                    {health.dhcp?.serverIP && <InfoPair label="DHCP Server" value={health.dhcp.serverIP} mono />}
                    {health.dhcp?.routerIP && <InfoPair label="DHCP Router" value={health.dhcp.routerIP} mono />}
                    {health.dhcp?.domainName && <InfoPair label="Domain" value={health.dhcp.domainName} />}
                    {health.dhcp?.dnsServers && health.dhcp.dnsServers.length > 0 && (
                      <InfoPair label="DHCP DNS" value={health.dhcp.dnsServers.join(', ')} mono />
                    )}
                    {activeInterface && (
                      <>
                        <InfoPair label="Interface" value={
                          activeInterface.friendlyName
                            ? `${activeInterface.friendlyName} (${activeInterface.name})`
                            : activeInterface.name
                        } mono />
                        {activeInterface.macAddress && (
                          <InfoPair label="Client MAC" value={activeInterface.macAddress} mono />
                        )}
                        {activeInterface.hardwareInfo?.productName && (
                          <InfoPair label="Adapter" value={
                            [activeInterface.hardwareInfo.vendorName, activeInterface.hardwareInfo.productName]
                              .filter(Boolean).join(' ')
                          } />
                        )}
                        {activeInterface.hardwareInfo?.chipsetName && (
                          <InfoPair label="Chipset" value={activeInterface.hardwareInfo.chipsetName} />
                        )}
                        {activeInterface.hardwareInfo?.actualLinkSpeed && (
                          <InfoPair label="Link Speed" value={activeInterface.hardwareInfo.actualLinkSpeed} />
                        )}
                        {activeInterface.addresses.length > 0 && (
                          <InfoPair label="IP" value={activeInterface.addresses[0]} mono />
                        )}
                      </>
                    )}
                  </dl>
                </SectionCard>
              )}

              {(health.vpn || dns) && (
                <SectionCard title="VPN & DNS">
                  <dl className="space-y-0.5">
                    {health.vpn && (
                      <>
                        <InfoPair label="VPN" value={health.vpn.isConnected ? 'Connected' : 'Disconnected'} />
                        <InfoPair label="Provider" value={health.vpn.providerType} />
                        <InfoPair label="Tunnel" value={health.vpn.tunnelMode} />
                      </>
                    )}
                    {dns && (
                      <>
                        {dns.primaryServers.length > 0 && (
                          <InfoPair label="DNS" value={dns.primaryServers.join(', ')} mono />
                        )}
                        {dns.hasCustomDNS && <InfoPair label="Custom DNS" value="Yes" />}
                        {dns.hasVPNResolver && <InfoPair label="VPN Resolver" value="Active" />}
                      </>
                    )}
                  </dl>
                </SectionCard>
              )}

              {/* ISP */}
              {(health?.observation?.publicIP || classifiedTrace?.clientASName) && (
                <SectionCard title="ISP">
                  <dl className="space-y-0.5">
                    {classifiedTrace?.clientASName && (
                      <InfoPair label="Provider" value={
                        classifiedTrace.clientASName.replace(/^AS-/, '').split(' - ')[0]
                      } />
                    )}
                    {classifiedTrace?.clientASN != null && (
                      <InfoPair label="ASN" value={`AS${classifiedTrace.clientASN}`} mono />
                    )}
                    <InfoPair label="Public IP" value={health?.observation?.publicIP} mono />
                    {classifiedTrace?.publicHostname && (
                      <InfoPair label="rDNS" value={classifiedTrace.publicHostname} mono />
                    )}
                    {firstIspHop?.pingStats30s && (
                      <>
                        <InfoPair label="First Hop RTT" value={`${firstIspHop.pingStats30s.avgLatencyMs}ms`} />
                        <InfoPair label="First Hop Loss" value={`${firstIspHop.pingStats30s.lossPercent}%`} />
                        {firstIspHop.pingStats30s.jitterMs != null && (
                          <InfoPair label="First Hop Jitter" value={`${firstIspHop.pingStats30s.jitterMs}ms`} />
                        )}
                      </>
                    )}
                  </dl>
                </SectionCard>
              )}
            </div>

            {/* RIGHT COLUMN: Device health */}
            <div className="space-y-4">
              {sysHealth && (
                <SectionCard title="System Health">
                  <div className="space-y-2.5 mb-3">
                    <GaugeRowDetailed
                      label="CPU"
                      value={sysHealth.cpuUsagePercent}
                      detail={sysHealth.cpuTemperatureCelsius != null ? `${Math.round(sysHealth.cpuTemperatureCelsius)}\u00b0C` : undefined}
                    />
                    <GaugeRowDetailed
                      label="Mem"
                      value={sysHealth.memoryUsedPercent}
                      detail={sysHealth.memoryTotalGB != null && sysHealth.memoryUsedGB != null
                        ? `${Math.round(sysHealth.memoryUsedGB)}/${sysHealth.memoryTotalGB} GB`
                        : undefined}
                      warn={sysHealth.memoryPressure === 'Warning' || sysHealth.memoryPressure === 'Critical'}
                    />
                    <GaugeRowDetailed
                      label="Disk"
                      value={sysHealth.diskUsedPercent}
                      detail={sysHealth.diskTotalGB != null && sysHealth.diskFreeGB != null
                        ? `${formatTB(sysHealth.diskFreeGB)} free / ${formatTB(sysHealth.diskTotalGB)}`
                        : undefined}
                    />
                    {sysHealth.batteryChargePercent != null && (() => {
                      const onAC = !!(sysHealth.adapterWatts && (sysHealth.adapterWatts as number) > 0)
                      return (
                        <GaugeRowDetailed
                          label="Bat"
                          value={sysHealth.batteryChargePercent}
                          detail={formatBatteryDetail(sysHealth)}
                          invert={!onAC}
                          forceOk={onAC}
                        />
                      )
                    })()}
                  </div>
                  <dl className="space-y-0.5 pt-2 border-t border-slate-700/30">
                    <InfoPair label="Thermal" value={sysHealth.thermalState} />
                    {sysHealth.storageTemperatureCelsius != null && (
                      <InfoPair label="SSD Temp" value={`${Math.round(sysHealth.storageTemperatureCelsius)}\u00b0C`} />
                    )}
                    <InfoPair label="Uptime" value={formatUptime(sysHealth.uptimeSeconds)} />
                    {sysHealth.isSwapping && (
                      <InfoPair label="Swap" value={`${sysHealth.swapUsedGB?.toFixed(1)} GB (active)`} />
                    )}
                  </dl>
                </SectionCard>
              )}

              {/* Tether */}
              {health.tether && (
                <SectionCard title="Tethered Connection">
                  <dl className="space-y-0.5">
                    <InfoPair label="Device" value={health.tether.deviceName} />
                    <InfoPair label="Network" value={health.tether.networkType} />
                    <InfoPair label="Metered" value={health.tether.isExpensive ? 'Yes' : 'No'} />
                  </dl>
                </SectionCard>
              )}

              {/* Captive Portal */}
              {health.captivePortal && (
                <SectionCard title="Captive Portal">
                  <dl className="space-y-0.5">
                    <div className="flex justify-between items-center py-1">
                      <dt className="text-slate-500 text-xs">Severity</dt>
                      <dd><SeverityBadge severity={health.captivePortal.severity} /></dd>
                    </div>
                    <InfoPair label="Detection" value={health.captivePortal.detectionMethod} />
                    <InfoPair label="Venue" value={health.captivePortal.venueName} />
                  </dl>
                </SectionCard>
              )}

              {/* Device metadata card (compact) */}
              <SectionCard title="Device Info">
                <dl className="space-y-0.5">
                  {machineName && <InfoPair label="Name" value={machineName} mono />}
                  {health?.metadata?.modelIdentifier && (
                    <InfoPair label="Model" value={health.metadata.modelIdentifier} mono />
                  )}
                  {health?.metadata?.chipName && (
                    <InfoPair label="Chip" value={health.metadata.chipName} />
                  )}
                  <InfoPair label="OS" value={`${device.os} ${device.osVersion}`} />
                  <InfoPair label="Version" value={`v${device.currentVersion}`} />
                  <InfoPair label="Channel" value={device.channel} />
                  <InfoPair label="Last Seen" value={formatLastSeen(device.lastSeen ?? device.lastCheckIn)} />
                  <InfoPair label="Check-ins" value={device.checkInCount.toLocaleString()} />
                  {(device.serialNumber || health?.metadata?.serialNumber) && (
                    <InfoPair label="Serial" value={device.serialNumber || health?.metadata?.serialNumber} mono />
                  )}
                  {en0MAC && <InfoPair label="WiFi MAC" value={en0MAC} mono />}
                  {device.assetId && <InfoPair label="Asset ID" value={device.assetId} mono />}
                </dl>
              </SectionCard>
            </div>
          </div>
        )}

        {/* ================================================================
            LOCATION & NETWORK (only when meaningful)
            ================================================================ */}
        {(hasLocation || device.network) && (
          <SectionCard title="Location & Network">
            <div className="flex flex-wrap items-center gap-3 mb-2">
              {hasLocation && device.location && (
                <>
                  <LocationBadge type={device.location.type} officeName={device.location.officeName} />
                  <span className="text-[10px] text-slate-600">{device.location.confidence}% confidence</span>
                </>
              )}
              {device.connectionState && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  device.connectionState === 'streaming' ? 'bg-emerald-500/10 text-emerald-400' :
                  device.connectionState === 'idle' ? 'bg-amber-500/10 text-amber-400' :
                  'bg-slate-700/50 text-slate-500'
                }`}>{device.connectionState}</span>
              )}
            </div>
            <dl className="space-y-0.5">
              {device.network?.ispName && <InfoPair label="ISP" value={device.network.ispName} />}
              {device.network?.networkType && <InfoPair label="Network" value={device.network.networkType} />}
              {device.network?.publicIp && <InfoPair label="Public IP" value={device.network.publicIp} mono />}
              {device.network?.ssid && <InfoPair label="SSID" value={device.network.ssid} mono />}
              {device.network?.gatewayVendor && device.network.gatewayVendor !== 'Unknown' && (
                <InfoPair label="Gateway" value={device.network.gatewayVendor} />
              )}
              {device.network?.radiusAuthenticated && <InfoPair label="RADIUS" value="Authenticated" />}
            </dl>
            {hasLocation && device.location && device.location.signals.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-slate-700/30">
                {device.location.signals.map((signal, i) => (
                  <span key={i} className="px-1.5 py-0.5 bg-slate-800/50 rounded text-[10px] text-slate-500">
                    {signal}
                  </span>
                ))}
              </div>
            )}
          </SectionCard>
        )}

        {/* User info (only when not already in header) */}
        {(device.userName || device.userEmail) && (
          <SectionCard title="User">
            <dl className="space-y-0.5">
              {device.userName && <InfoPair label="Name" value={device.userName} />}
              {device.userEmail && <InfoPair label="Email" value={device.userEmail} mono />}
            </dl>
          </SectionCard>
        )}

        {/* If no health snapshot, show device metadata inline */}
        {!health && (
          <>
            <SectionCard title="System Information">
              <dl className="space-y-0.5">
                <InfoPair label="Operating System" value={`${device.os} ${device.osVersion}`} />
                <InfoPair label="Architecture" value={device.arch} />
                <InfoPair label="App Version" value={`v${device.currentVersion}`} />
                <InfoPair label="Update Channel" value={device.channel} />
              </dl>
            </SectionCard>

            <SectionCard title="Activity">
              <dl className="space-y-0.5">
                <InfoPair label="Last Check-in" value={formatLastSeen(device.lastSeen ?? device.lastCheckIn)} />
                {device.lastTelemetry && <InfoPair label="Last Telemetry" value={formatLastSeen(device.lastTelemetry)} />}
                <InfoPair label="Check-in Count" value={device.checkInCount.toLocaleString()} />
              </dl>
            </SectionCard>

            {(device.serialNumber || device.assetId) && (
              <SectionCard title="Identifiers">
                <dl className="space-y-0.5">
                  {device.assetId && <InfoPair label="Asset ID" value={device.assetId} mono />}
                  {device.serialNumber && <InfoPair label="Serial Number" value={device.serialNumber} mono />}
                </dl>
              </SectionCard>
            )}
          </>
        )}

        {/* Activity history from API */}
        {(device.activity ?? []).length > 0 && (
          <SectionCard title="Recent Activity">
            <div className="space-y-0">
              {(device.activity ?? []).map((item, i) => (
                <div key={i} className="flex items-start gap-3 text-xs py-1.5 border-b border-slate-800/30 last:border-0">
                  <span className="text-slate-600 font-mono w-14 shrink-0">{formatLastSeen(item.timestamp)}</span>
                  <span className="text-slate-300 capitalize">{item.event.replace('-', ' ')}</span>
                  <span className="text-slate-500 ml-auto text-right">{item.detail}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Telemetry Logs */}
        <DeviceLogsSection clientId={device.clientId} />
      </div>
    </div>
  )
}

function formatBatteryDetail(sys: Record<string, unknown>): string {
  const adapterWatts = sys.adapterWatts as number | null
  const batteryWatts = sys.batteryPowerWatts as number | null
  const cycleCount = sys.batteryCycleCount as number | null
  const designCycles = sys.batteryDesignCycleCount as number | null
  const healthPct = sys.batteryHealthPercent as number | null
  const tempC = sys.batteryTemperatureCelsius as number | null
  const isLowPower = sys.isLowPowerMode as boolean | null

  const parts: string[] = []

  // Charge state + power
  if (adapterWatts && adapterWatts > 0) {
    if (batteryWatts != null && batteryWatts > 0) {
      parts.push(`Charging @${adapterWatts}W`)
    } else {
      parts.push(`AC ${adapterWatts}W`)
    }
  } else {
    parts.push('On battery')
  }

  // Temperature
  if (tempC != null) parts.push(`${Math.round(tempC)}\u00b0C`)

  // Health + cycles
  if (cycleCount != null && designCycles != null && designCycles > 0) {
    const cycleStr = `${cycleCount}/${designCycles} cycles`
    if (healthPct != null) {
      parts.push(`${healthPct}% health, ${cycleStr}`)
    } else {
      parts.push(cycleStr)
    }
  }

  if (isLowPower) parts.push('low power')

  return parts.join(', ')
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  if (days > 0) return `${days}d ${hours}h`
  const mins = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

// Severity levels for filtering
const severityLevels = ['DEFAULT', 'DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL'] as const

const severityColors: Record<string, string> = {
  DEFAULT: 'text-slate-400',
  DEBUG: 'text-slate-500',
  INFO: 'text-blue-400',
  NOTICE: 'text-cyan-400',
  WARNING: 'text-amber-400',
  ERROR: 'text-red-400',
  CRITICAL: 'text-red-500 font-semibold',
}

function DeviceLogsSection({ clientId }: { clientId: string }) {
  const [logs, setLogs] = useState<DeviceLogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [severity, setSeverity] = useState<string>('')
  const [expanded, setExpanded] = useState(false)

  const fetchLogs = (sev?: string) => {
    setLoading(true)
    setError(null)

    const params: { limit: number; severity?: string } = { limit: 100 }
    const activeSeverity = sev ?? severity
    if (activeSeverity) params.severity = activeSeverity

    api.getDeviceLogs(clientId, params)
      .then(res => setLogs(res.data ?? []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchLogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId])

  const handleSeverityChange = (newSeverity: string) => {
    setSeverity(newSeverity)
    fetchLogs(newSeverity)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Telemetry Logs</h3>
        <div className="flex items-center gap-2">
          <select
            value={severity}
            onChange={e => handleSeverityChange(e.target.value)}
            className="bg-slate-800 border border-slate-700 text-slate-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-amber-500/50"
          >
            <option value="">All severities</option>
            {severityLevels.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={() => fetchLogs()}
            disabled={loading}
            className="px-3 py-1 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-800/50 text-slate-300 rounded text-xs transition-colors"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {logs.length > 0 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="px-2 py-1 text-slate-500 hover:text-slate-300 text-xs transition-colors"
            >
              {expanded ? 'Collapse' : 'Expand'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {loading && logs.length === 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
          <div className="animate-pulse space-y-2">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-4 bg-slate-800 rounded w-full" />
            ))}
          </div>
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div className={`bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden ${expanded ? '' : 'max-h-64'} overflow-y-auto`}>
          <div className="divide-y divide-slate-800/50">
            {logs.map((log, i) => (
              <div key={i} className="px-4 py-2 flex items-start gap-3 text-xs hover:bg-slate-800/30">
                <span className="text-slate-600 font-mono shrink-0 w-40">
                  {new Date(log.timestamp).toLocaleString()}
                </span>
                <span className={`font-mono shrink-0 w-16 ${severityColors[log.severity] || 'text-slate-400'}`}>
                  {log.severity}
                </span>
                <pre className="text-slate-300 font-mono whitespace-pre-wrap break-all flex-1">
                  {typeof log.payload === 'string' ? log.payload : JSON.stringify(log.payload, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && logs.length === 0 && !error && (
        <div className="text-slate-600 text-sm">
          No telemetry logs found for this device.
        </div>
      )}
    </div>
  )
}
